import classNames from 'classnames';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { CookieEnum, DEFAULT_COOKIE_PREFERENCES, PRIVACY_POLICY_URL } from 'constants/Cookies';
import { ucFirst } from 'utils/global';

import Footer from './Footer';
import Preference from './Preference';
import { Link, Modal, bannerCSS } from './UI';

const ANIMATION_CONFIG = { animationDuration: '1s' };

const getLink = chunks => (
  <Link href={PRIVACY_POLICY_URL} target="_blank">
    {chunks}
  </Link>
);

/**
 * @typedef {Object} CookieModalProps
 * @property {function} onClose
 * @property {Record<import('constants/Cookies').CookieType, boolean>} defaultPreferences
 */

/** @type {React.FC<CookieModalProps>} */
const CookiesModal = ({ onClose, defaultPreferences = DEFAULT_COOKIE_PREFERENCES }) => {
  const intl = useIntl();

  const [preferences, setPreferences] = React.useState(defaultPreferences);
  const [showPreferences, setShowPreferences] = React.useState(false);

  const title = React.useMemo(() => (
    showPreferences
      ? <FormattedMessage id="cookies.settingsTitle" defaultMessage="Privacy preferences" />
      : <FormattedMessage id="cookies.title" defaultMessage="We value your privacy" />
  ), [showPreferences]);

  const toggleShowPreferences = React.useCallback(() => setShowPreferences(oldShow => !oldShow), []);
  const handleTogglePreference = React.useCallback(
    type => () =>
      setPreferences(oldPreferences => ({
        ...oldPreferences,
        [type]: !oldPreferences[type],
      })),
    []
  );

  const savePreferences = React.useCallback(
    newPreferences => {
      setPreferences(newPreferences);
      onClose?.(newPreferences);
    },
    [setPreferences, onClose]
  );

  const handleSavePreferences = React.useCallback(() => savePreferences(preferences), [savePreferences, preferences]);

  const handleAcceptOnlyEssential = React.useCallback(
    () => savePreferences(DEFAULT_COOKIE_PREFERENCES),
    [savePreferences]
  );

  const handleAcceptAll = React.useCallback(
    () =>
      savePreferences(
        Object.values(CookieEnum).reduce(
          (acc, type) => ({
            ...acc,
            [type]: true,
          }),
          {}
        )
      ),
    [savePreferences]
  );

  return (
    <Modal
      open
      large
      style={ANIMATION_CONFIG}
      transitionName="move-down"
      title={title}
      className={classNames({ [bannerCSS]: !showPreferences })}
      footer={
        <Footer
          onAcceptAll={handleAcceptAll}
          showSavePreferences={showPreferences}
          onPreferencesSave={handleSavePreferences}
          onPreferencesChange={toggleShowPreferences}
          onAcceptEssential={handleAcceptOnlyEssential}
        />
      }
    >
      <div data-cy="cookies-modal">
        {!showPreferences && (
          <FormattedMessage
            id="cookies.content"
            defaultMessage='Fleetback uses cookies and other technologies to keep the website reliable and secure, and to measure performance. To do this, we collect information about users, their behaviour, and their devices.{linebreak}{linebreak}If you select "Accept all", you accept this and agree that we may share this information with third parties, such as our marketing partners and analytics services. Select "Set preferences" for further details and to manage your options.{linebreak}{linebreak}For more information, please read our <a>privacy notice</a>.'
            values={{
              a: getLink,
              linebreak: <br />,
            }}
          />
        )}
        {showPreferences &&
          Object.values(CookieEnum).map(type => (
            <Preference
              key={type}
              checked={preferences[type]}
              data-cy={`cookies-switch-${type}`}
              onChange={handleTogglePreference(type)}
              disabled={type === CookieEnum.ESSENTIAL}
              title={<FormattedMessage id={`cookies.${type.toLowerCase()}Title`} defaultMessage={ucFirst(type)} />}
              description={
                <FormattedMessage id={`cookies.${type.toLowerCase()}Description`} defaultMessage={ucFirst(type)} />
              }
            />
          ))}
      </div>
    </Modal>
  );
};

export default CookiesModal;
